import type { ComponentProps } from 'react';
import React from 'react';

import Button, { type ButtonProps } from './Button';

type ButtonUploadProps = Omit<ButtonProps, 'onClick'> & {
  accept?: ComponentProps<'input'>['accept'];
  multiple?: ComponentProps<'input'>['multiple'];
  onOpenFile?: (file: File) => void;
};

export default function ButtonUpload({ children, onOpenFile, accept, multiple, ...props }: ButtonUploadProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    // Don't gate on MIME type here — the `accept` prop already restricts what the
    // picker offers, and the file's reported `type` is unreliable (e.g. an Apple
    // `.numbers` file is `application/vnd.apple.numbers` or empty, and `.xlsx` has
    // its own MIME). Hardcoding `text/csv` silently dropped every non-CSV file.
    if (file && onOpenFile != null) {
      onOpenFile(file);
    }
    // Reset so picking the same file again still fires onChange.
    event.target.value = '';
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  return (
    <Button {...props} onClick={openFilePicker}>
      {children}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        accept={accept}
        multiple={multiple}
      />
    </Button>
  );
}
